import React, { useState, useEffect, useCallback } from 'react';
import { TreeSelect } from 'antd';

interface SelectValue {
  label: string;
  value: string;
}

interface UserSelectionProps {
  users: { email: string; azureObjectId: string }[];
  selectedUsers: SelectValue[];
  handleChangeSelectedUsers: (selectedUsers: SelectValue[]) => void;
}

const UserSelection: React.FC<UserSelectionProps> = ({
  users,
  selectedUsers,
  handleChangeSelectedUsers,
}) => {
  const [internalSelectedUsers, setInternalSelectedUsers] = useState<SelectValue[]>(selectedUsers);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    setInternalSelectedUsers(selectedUsers);
  }, [selectedUsers]);

  const groupUsersByDomain = useCallback((users: { email: string; azureObjectId: string }[]) => {
    type GroupedUsers = {
      [domain: string]: { email: string; azureObjectId: string }[];
    };

    const grouped: GroupedUsers = users.reduce((acc: GroupedUsers, user) => {
      const domain = user.email.split('@')[1];
      if (!acc[domain]) {
        acc[domain] = [];
      }
      acc[domain].push(user);
      return acc;
    }, {});

    const sortedDomains = Object.keys(grouped).sort();

    return sortedDomains.flatMap((domain) => {
      const usersInDomain = grouped[domain];
      const sortedUsersInDomain = usersInDomain.sort((a, b) => a.email.localeCompare(b.email));

      if (sortedUsersInDomain.length === 1) {
        const user = sortedUsersInDomain[0];
        return {
          title: user.email,
          value: user.azureObjectId,
          key: user.azureObjectId,
        };
      } else {
        return {
          title: domain,
          value: domain,
          key: domain,
          children: sortedUsersInDomain.map((user) => ({
            title: user.email,
            value: user.azureObjectId,
            key: user.azureObjectId,
          })),
        };
      }
    });
  }, []);

  const handleChange = (value: SelectValue[]) => {
    const selected = value.map((val) => ({
      label: val.label,
      value: val.value,
    }));
    setInternalSelectedUsers(selected);
    if (!isDropdownVisible) {
      handleChangeSelectedUsers(selected);
    }
  };

  const handleDropdownVisibleChange = (visible: boolean) => {
    setIsDropdownVisible(visible);
    if (!visible) {
      handleChangeSelectedUsers(internalSelectedUsers);
    }
  };

  const handleSearch = (input: string) => {
    let workingInput = input;
    let newSelected = [...internalSelectedUsers];
    let changed = false;

    // Regex to match segments ending with , ; or whitespace
    const regex = /([^,;\s]+)\s*([,;\s])/g;
    let match;
    let segmentsToRemove: string[] = [];

    while ((match = regex.exec(input)) !== null) {
      const email = match[1].trim();
      const user = users.find((u) => u.email === email);
      if (user && !newSelected.some((sel) => sel.value === user.azureObjectId)) {
        newSelected.push({ label: user.email, value: user.azureObjectId });
        changed = true;
        // Mark this segment (with its separator) for removal
        segmentsToRemove.push(match[0]);
      }
    }

    // Remove matched segments from input
    segmentsToRemove.forEach((seg) => {
      workingInput = workingInput.replace(seg, '');
    });

    setSearchValue(workingInput);
    if (changed) {
      setInternalSelectedUsers(newSelected);
      handleChangeSelectedUsers(newSelected);
    }
  };

  return (
    <TreeSelect
      treeData={groupUsersByDomain(users)}
      value={internalSelectedUsers}
      labelInValue={true}
      onChange={handleChange}
      treeCheckable={true}
      placeholder="Bitte Benutzer auswählen"
      allowClear={true}
      style={{ marginTop: '12px', width: '555px' }}
      showSearch
      filterTreeNode={(inputValue, treeNode) => {
        const title = treeNode.title;
        if (typeof title === 'string') {
          return title.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0;
        }
        return false;
      }}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onSearch={handleSearch}
      searchValue={searchValue}
    />
  );
};

export default UserSelection;
